import { Box, Stack, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';

import { Button } from '@/components/form/baseInputs/Button';
import { FormattedNumberInput } from '@/components/form/baseInputs/FormattedNumberInput';
import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys';
import { COLORS } from '@/styles/tokens/colors';
import { localStorageBooleanSerializer } from '@/utils/localStorageSerializers';

import { useRedirectIfEmailMissing } from '../hooks/useRedirectIfEmailMissing';

const defaultRedirectPath = '/';
const invalidCodeErrorMessage =
  'Failed to log in. Please check the code and try again.';
const loginErrorMessage = 'Failed to log in. Please try again.';

interface MagicCodeCallbackResponse {
  redirect_path?: unknown;
}

function getSafeRedirectPath(redirectPath: unknown): string {
  if (typeof redirectPath !== 'string') {
    return defaultRedirectPath;
  }

  try {
    const url = new URL(redirectPath, window.location.origin);
    if (url.origin !== window.location.origin) {
      return defaultRedirectPath;
    }

    // Guard against an empty result (e.g. a same-origin URL with no path),
    // which would make window.location.assign reload the current page.
    const safePath = `${url.pathname}${url.search}${url.hash}`;
    return safePath || defaultRedirectPath;
  } catch {
    return defaultRedirectPath;
  }
}

async function getMagicCodeRedirectPath(response: Response): Promise<string> {
  // The callback returns 200 {"redirect_path": ...}; we navigate there so the
  // browser follows the subsequent connect -> WorkOS redirect chain natively.
  const contentType = response.headers.get('Content-Type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      const data = (await response.json()) as MagicCodeCallbackResponse;
      return getSafeRedirectPath(data.redirect_path);
    } catch {
      // The 200 response already set the session cookie, so a malformed body
      // shouldn't surface as a login failure. Fall back to the default path.
      return defaultRedirectPath;
    }
  }

  return defaultRedirectPath;
}

interface CodeChallengeFormProps {
  email: string | null;
}

export function CodeChallengeForm({ email }: CodeChallengeFormProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [rememberExtendedSession, setRememberExtendedSession] =
    useLocalStorage<boolean>(
      LOCAL_STORAGE_KEYS.REMEMBER_EXTENDED_SESSION,
      false,
      localStorageBooleanSerializer
    );

  // this pattern seems funny, but the email is only available on the client, not on the server,
  // and we need to render it after a loop to satisfy the SSR hydration pattern gods.
  // see here: https://nextjs.org/docs/messages/react-hydration-error
  const [postMountEmail, setPostMountEmail] = useState<string | null>(null);
  useEffect(() => {
    if (email) {
      setPostMountEmail(email);
    }
  }, [email]);

  // Use the redirect hook to ensure we have an email
  useRedirectIfEmailMissing(email);

  const handleCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers
    if (/^\d*$/.test(e.target.value)) {
      setCode(e.target.value);
      setError(null);
    }
  };

  const submitCode = async () => {
    setError(null);
    setIsSubmitting(true);

    if (!postMountEmail) {
      setError(invalidCodeErrorMessage);
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/v2/magic-code', {
        method: 'POST',
        redirect: 'manual',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
          email: postMountEmail,
          remember_extended_session: rememberExtendedSession ?? false,
        }),
      });

      if (response.status === 401) {
        setError(invalidCodeErrorMessage);
        setIsSubmitting(false);
        return;
      }

      if (
        !response.ok &&
        response.type !== 'opaqueredirect' &&
        (response.status < 300 || response.status >= 400)
      ) {
        setError(loginErrorMessage);
        setIsSubmitting(false);
        return;
      }

      const redirectPath = await getMagicCodeRedirectPath(response);
      setIsSubmitSuccessful(true);
      setRememberExtendedSession(false);
      window.location.assign(redirectPath);
    } catch {
      setError(loginErrorMessage);
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submitCode();
  };

  return (
    <Stack
      textAlign="center"
      spacing={4}
      component="form"
      onSubmit={handleSubmit}
    >
      <Typography variant="body-s" component="p">
        A one-time password has been sent to{' '}
        <Typography
          variant="body-s"
          sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
          component="span"
        >
          {postMountEmail ?? 'your email'}
        </Typography>
        . Please enter it below.
      </Typography>
      <Box textAlign="left">
        <FormattedNumberInput
          label="One-time password"
          required
          value={code}
          onChange={handleCodeChange}
          disabled={isSubmitting || isSubmitSuccessful || !postMountEmail}
          // https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#one-time-code
          // https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#attr-fe-autocomplete-one-time-code
          autoComplete="one-time-code"
          placeholder="______"
          formatHashString="###-###"
          name="code-challenge-form-input"
          sx={{
            '& .MuiOutlinedInput-input': {
              textAlign: 'center !important',
              fontSize: '24px !important',
            },
          }}
        />
      </Box>
      {error && (
        <Typography
          variant="body-s"
          component="p"
          color={COLORS.FUNCTIONAL.ERROR.DEFAULT}
          data-testid="code-challenge-form-error"
        >
          {error}
        </Typography>
      )}
      {isSubmitSuccessful ? (
        <Typography
          variant="body-s"
          component="p"
          data-testid="code-challenge-form-success"
        >
          Success! Continuing to Luminary...
        </Typography>
      ) : (
        <Button
          fullWidth
          loading={isSubmitting}
          disabled={isSubmitting}
          variant="primary"
          size="sm"
          type="submit"
          data-testid="code-challenge-form-submit"
        >
          Continue
        </Button>
      )}
    </Stack>
  );
}
